import { useState } from 'react';
import { Button, Input, InputGroup, InputGroupAddon } from 'reactstrap';
import useCallApiAndLoad from '../../../../hooks/useCallApiAndLoad';
import useAuth from '../../../../hooks/useAuth';
import { getLandlordByMailEndpoint } from '../../../../services/landlordService';
import { fetchResponseAdapter } from '../../../../adapters/fetchAdapter';

interface Props {
  currentName?: string;
  currentEmail?: string;
  onOwnerFound: (landlordId: string) => void;
  onOwnerCleared: () => void;
}

interface FoundOwner {
  landlord_id: string;
  name: string;
  email: string;
  phone: string;
}

const OwnerField = ({
  currentName,
  currentEmail,
  onOwnerFound,
  onOwnerCleared,
}: Props) => {
  const { callEndpoint, isLoading } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [isSearching, setIsSearching] = useState(!currentName);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [found, setFound] = useState<FoundOwner>();

  const search = async () => {
    setError('');
    if (!email.trim()) return;
    const { status, data } = await callEndpoint(
      getLandlordByMailEndpoint(email.trim(), token),
    );
    if (status !== 200) {
      setError('El propietario/inversionista no fue encontrado.');
      return;
    }
    const owner: FoundOwner = fetchResponseAdapter(data).data;
    setFound(owner);
    onOwnerFound(owner.landlord_id);
  };

  const reset = () => {
    setEmail('');
    setError('');
    setFound(undefined);
    onOwnerCleared();
    setIsSearching(!currentName);
  };

  const owner = found ?? { name: currentName, email: currentEmail };

  return (
    <div>
      {owner.name && (
        <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
          <div>
            <h5 className="mb-0">
              {owner.name}
              {found && (
                <span className="badge badge-warning ml-2">Nuevo</span>
              )}
            </h5>
            <small className="text-muted">
              {found ? found.phone : owner.email}
            </small>
          </div>
          {!isSearching && (
            <Button
              type="button"
              size="sm"
              color="secondary"
              onClick={() => setIsSearching(true)}
            >
              Cambiar
            </Button>
          )}
        </div>
      )}
      {isSearching && (
        <>
          <InputGroup>
            <Input
              type="text"
              className={error ? 'is-invalid' : ''}
              placeholder="Correo del nuevo propietario"
              value={email}
              onChange={({ target }) => setEmail(target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                search();
              }}
            />
            <InputGroupAddon addonType="append">
              <Button
                type="button"
                color="primary"
                disabled={isLoading || !email.trim()}
                onClick={search}
              >
                Buscar
              </Button>
              {(currentName || found) && (
                <Button
                  type="button"
                  color="secondary"
                  outline
                  aria-label="Cancelar cambio de propietario"
                  onClick={reset}
                >
                  <i className="fa-solid fa-xmark" />
                </Button>
              )}
            </InputGroupAddon>
          </InputGroup>
          {error && <h5 className="text-danger mt-2">{error}</h5>}
        </>
      )}
    </div>
  );
};

export default OwnerField;
